import React, { ReactNode, useCallback, useState } from 'react';
import * as css from './dropdown.module.scss';
import Icon, { IconType } from '../icon';
import classNames from 'classnames';
import translations from './autocomplete.i18n';
import { useIntl } from 'react-intl';
import Button from '../button';
import { OnStedHoverCard, StedType } from '../hierarchy/onhoverhierarchyinfo';

export interface DropdownOption {
  navn: string;
  id: string;
  deaktivert?: boolean;
  checked?: boolean;
  hideDisabledCheckbox?: boolean;
  tooltipText?: string;
  helpButton?: ReactNode;
  showHelpButton?: boolean;
  onHoverType?: StedType;
  alert?: boolean;
}

interface DropdownOptionFull extends DropdownOption {
  type?: string;
  onClick?: () => void;
  onKeyPress?: () => void;
}

interface TextInputProps {
  value: string;
  htmlId: string;
  toggled: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  setToggle: (value: boolean) => void;
  setInputFocus: () => void;
  currentOptionId: string;
  ariaLabel?: string;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  required?: boolean;
  multiSelectable?: boolean;
  hideBorder?: boolean;
  hideBackground?: boolean;
  highlightPlaceholder?: boolean;
  alert?: boolean;
  dataCy?: string;
}

const DropdownTextInput: React.FC<React.PropsWithChildren<TextInputProps>> = ({
  value,
  htmlId,
  ariaLabel,
  setInputFocus,
  disabled,
  placeholder,
  label,
  required,
  toggled,
  setToggle,
  inputRef,
  currentOptionId,
  multiSelectable,
  hideBorder,
  hideBackground,
  highlightPlaceholder,
  alert,
  dataCy,
}) => {
  const [fakeHoverLabel, setFakeHoverLabel] = useState<boolean>(false);
  const [fakeHoverBorder, setFakeHoverBorder] = useState<boolean>(false);

  const iconDirection = toggled
    ? IconType.CloseRowTriangle
    : IconType.OpenRowTriangle;

  const getStateSpecifiedStyles = () => [
    highlightPlaceholder ? css.highlightPlaceholder : '',
    hideBorder ? css.hideBorder : '',
    hideBackground && !toggled ? css.hideBackground : '',
    alert ? css.alert : '',
  ];

  const getLabelStyle = () =>
    !label || label.length === 0 ? css.noLabel : css.hasLabel;

  return (
    <div className={css.input}>
      <div
        className={classNames(
          css.textInputContainer,
          disabled ? css.disabled : '',
        )}
      >
        <span
          className={classNames(
            label ? css.textInputLabel : '',
            getLabelStyle(),
            fakeHoverLabel ? css.fakeHoverLabel : '',
          )}
        >
          {label && (
            <label htmlFor={htmlId}>
              {label} {required && <span className={css.required}>*</span>}
            </label>
          )}
          <input
            id={htmlId}
            tabIndex={0}
            className={classNames(
              css.textInput,
              toggled ? css.clicked : '',
              getLabelStyle(),
              fakeHoverBorder ? css.fakeHoverBorder : css.textInputBorder,
              ...getStateSpecifiedStyles(),
            )}
            value={value}
            placeholder={placeholder || ''}
            disabled={disabled}
            type={'text'}
            required={required}
            autoComplete={'off'}
            ref={inputRef}
            onKeyDownCapture={(e) => {
              if (['ArrowUp', 'Up'].includes(e.key)) setToggle(false);
            }}
            role={'listbox'}
            aria-expanded={toggled}
            aria-label={ariaLabel ? ariaLabel : `${label} dropdown menu`}
            aria-required={required}
            aria-activedescendant={currentOptionId}
            aria-multiselectable={multiSelectable}
            readOnly
            data-cy={dataCy ? dataCy : 'dropdown-input'}
          />
        </span>
      </div>
      <div
        className={classNames(
          css.arrow,
          disabled ? css.disabled : '',
          hideBackground ? css.hideBackground : '',
        )}
        tabIndex={0}
        role={'button'}
        aria-pressed={toggled}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            setInputFocus();
          } else if (e.key === 'Tab') {
            setToggle(false);
          }
        }}
        onClick={() => {
          setInputFocus();
        }}
        onMouseEnter={() => {
          setFakeHoverLabel(true);
          setFakeHoverBorder(true);
        }}
        onMouseLeave={() => {
          setFakeHoverLabel(false);
          setFakeHoverBorder(false);
        }}
        onFocusCapture={() => {
          setFakeHoverBorder(true);
        }}
        onBlurCapture={() => {
          setFakeHoverBorder(false);
        }}
      >
        <Icon type={iconDirection} className={css.arrowSvg} />
      </div>
    </div>
  );
};

const DropdownOptionComponent: React.FC<
  React.PropsWithChildren<{
    option: DropdownOptionFull;
    renderedOption?: ReactNode;
    onChange: (
      event: React.MouseEvent | React.KeyboardEvent,
      value: DropdownOptionFull,
    ) => void;
    keyDownHandler: (
      event: React.KeyboardEvent,
      option: DropdownOptionFull,
    ) => void;
    activeRef?: React.RefObject<HTMLDivElement>;
    checked?: boolean;
    currentValue?: string;
    multiple?: boolean;
  }>
> = ({
  option,
  renderedOption,
  onChange,
  keyDownHandler,
  activeRef,
  checked,
  currentValue,
  multiple,
}) => {
  const onChangeHandler = (event: React.MouseEvent | React.KeyboardEvent) => {
    if (option.deaktivert) {
      event.stopPropagation();
      return;
    }

    onChange(event, option);
  };

  return (
    <div
      className={classNames(
        css.dropdownitem,
        (option.deaktivert || !option.id) && css.disabledItem,
        option.alert && css.alert,
      )}
      data-cy="dropdown-input-option"
      onClick={(e) => onChangeHandler(e)}
      onKeyDown={(e) => {
        keyDownHandler(e, option);
      }}
      tabIndex={-1}
      ref={activeRef}
      role={'option'}
      aria-selected={checked || option.navn === currentValue}
      id={option.id}
      title={option.tooltipText}
    >
      {multiple &&
        !(option.deaktivert && option.hideDisabledCheckbox) &&
        (checked ? (
          <Icon
            type={IconType.CheckBox}
            className={option.deaktivert ? css.checkboxDisabled : css.checkbox}
          />
        ) : (
          <Icon
            type={IconType.CheckBoxOutline}
            className={
              option.deaktivert ? css.checkboxDisabled : css.checkboxOutline
            }
          />
        ))}
      {option.onHoverType !== undefined ? (
        <div className={css.dropdownItemText}>
          {renderedOption || option.navn}
        </div>
      ) : (
        <>{renderedOption || option.navn}</>
      )}
      <>
        {option.onHoverType !== undefined && (
          <div className={css.helpButton}>
            <OnStedHoverCard
              sted={option.onHoverType}
              id={option.id}
              navn={option.navn}
            />
          </div>
        )}
      </>
    </div>
  );
};

const DropdownButton: React.FC<
  React.PropsWithChildren<{
    buttonRef: React.RefObject<HTMLDivElement>;
    keyDownHandler: (event: React.KeyboardEvent, value: undefined) => void;
    text?: string;
    onChange?: (event: object) => void;
  }>
> = ({ text, onChange, buttonRef, keyDownHandler }) => {
  const onChangeHandler = (event: React.MouseEvent | React.KeyboardEvent) => {
    if (!onChange) return;

    onChange(event);
  };

  return (
    <div className={css.dropdownButtonContainer} ref={buttonRef}>
      <Button.Secondary
        className={css.button}
        type={'button'}
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          onChangeHandler(event);
        }}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === 'Enter') {
            event.stopPropagation();
            onChangeHandler(event);
          } else {
            keyDownHandler(event, undefined);
          }
        }}
      >
        {text}
      </Button.Secondary>
    </div>
  );
};

const DropdownMenu: React.FC<
  React.PropsWithChildren<{
    id: string;
    onChange: (
      event: React.MouseEvent | React.KeyboardEvent,
      value: DropdownOptionFull,
    ) => void;
    setInputFocus: () => void;
    activateDropdownNavigation: (value?: boolean) => void;
    currentValue?: string;
    activated?: boolean;
    options?: DropdownOptionFull[];
    renderOption?: (option: DropdownOptionFull) => ReactNode;
    noOptionsText?: string;
    loading?: boolean;
    multiple?: boolean;
    button?: boolean;
    buttonText?: string;
    buttonOnChange?: (event: object) => void;
    tooltipText?: string;
  }>
> = ({
  id,
  options,
  onChange,
  renderOption,
  activated,
  setInputFocus,
  activateDropdownNavigation,
  noOptionsText,
  loading,
  multiple,
  currentValue,
  button,
  buttonText,
  buttonOnChange,
  tooltipText,
}) => {
  const [focusIndex, setFocusIndex] = useState<number>(-1);
  const selectRef = React.useRef<HTMLDivElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLDivElement>(null);

  const onKeyDownHandler = (
    event: React.KeyboardEvent,
    option: DropdownOptionFull | undefined,
  ) => {
    let tempIndex = focusIndex;

    switch (event.key) {
      case 'ArrowDown':
      case 'Down':
        if (buttonRef && (!options || tempIndex === options.length - 1)) {
          setFocusIndex((tempIndex += 1));

          buttonRef.current &&
            buttonRef.current.firstElementChild &&
            (buttonRef.current.firstElementChild as HTMLButtonElement).focus();
        }
        while (options && tempIndex < options.length - 1) {
          tempIndex += 1;

          if (options[tempIndex].deaktivert) {
            continue;
          }

          setFocusIndex(tempIndex);
          break;
        }
        break;
      case 'ArrowUp':
      case 'Up':
        if (options && focusIndex > options.length) tempIndex = options.length;
        while (options && focusIndex > -1) {
          tempIndex -= 1;

          if (tempIndex === -1) {
            setFocusIndex(-1);
            setInputFocus();
            activateDropdownNavigation(false);
            break;
          }

          if (options[tempIndex].deaktivert) {
            continue;
          }

          setFocusIndex(tempIndex);
          break;
        }
        break;
      case 'Enter':
        if (option) onChange(event, option);

        if (!multiple) {
          activateDropdownNavigation(false);
        }

        setInputFocus();
        break;
      default:
        setFocusIndex(-1);
        activateDropdownNavigation(false);
        setInputFocus();
    }

    selectRef.current &&
      selectRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  let key = 0;
  const formattedOptions =
    options &&
    options.map((option) => (
      <DropdownOptionComponent
        onChange={onChange}
        option={option}
        activeRef={focusIndex === key ? selectRef : undefined}
        key={key++}
        keyDownHandler={onKeyDownHandler}
        renderedOption={renderOption && renderOption(option)}
        checked={multiple && option.checked}
        currentValue={currentValue}
        multiple={multiple}
      />
    ));

  const setFocus = () => {
    selectRef.current && selectRef.current.focus();
  };

  React.useEffect(() => {
    if (activated && focusIndex === -1)
      setFocusIndex(
        options
          ? options.indexOf(
              options.find(
                (option) => !option.deaktivert,
              ) as DropdownOptionFull,
            )
          : 0,
      );
    if (activated) setFocus();
  }, [activated, focusIndex, selectRef, options]);

  const { formatMessage } = useIntl();
  const noOptionsDefaultText = formatMessage({
    id: translations['noOptions'].id,
  });
  const loadingText = formatMessage({
    id: translations['loading'].id,
  });

  const emptySelect = loading
    ? loadingText
    : noOptionsText || noOptionsDefaultText;

  return (
    <div
      className={classNames(css.dropdownmenu, css.dropdownBorder)}
      ref={dropdownRef}
      id={id}
    >
      {!loading &&
      options &&
      formattedOptions &&
      formattedOptions.length > 0 ? (
        formattedOptions
      ) : (
        <DropdownOptionComponent
          onChange={onChange}
          option={{ id: 'empty', navn: emptySelect, deaktivert: true }}
          keyDownHandler={() => {}}
        />
      )}
      {button && (
        <DropdownButton
          text={buttonText}
          onChange={buttonOnChange}
          buttonRef={buttonRef}
          keyDownHandler={onKeyDownHandler}
        />
      )}
    </div>
  );
};

interface DropdownProps {
  id: string;
  onChange: (event: object, value: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  renderOption?: (option: any) => React.ReactNode; // eslint-disable-line @typescript-eslint/no-explicit-any
  options?: DropdownOption[];
  loading?: boolean;
  disabled?: boolean;
  noOptionsText?: string;
  placeholder?: string;
  required?: boolean;
  defaultValue?: DropdownOption | DropdownOption[];
  multiple?: boolean;
  hideBorder?: boolean;
  hideBackground?: boolean;
  highlightPlaceholder?: boolean;
  alert?: boolean;
  button?: boolean;
  buttonText?: string;
  buttonOnChange?: (event: object) => void;
  tooltipText?: string;
  className?: string;
  dataCy?: string;
  separator?: string;
}

interface DropdownWithLabel extends DropdownProps {
  label: string;
  ariaLabel?: never;
}

interface DropdownWithAriaLabel extends DropdownProps {
  ariaLabel: string;
  label?: never;
}

interface SelectedOption {
  selectedOption?: DropdownOption;
  selectedOptions?: never;
}

interface SelectedOptions {
  selectedOptions?: DropdownOption[];
  selectedOption?: never;
}

type Options = SelectedOption | SelectedOptions;

type DropdownType = (DropdownWithLabel | DropdownWithAriaLabel) & Options;

const Dropdown: React.FC<React.PropsWithChildren<DropdownType>> = ({
  id,
  onChange,
  label,
  options,
  loading,
  disabled,
  noOptionsText,
  renderOption,
  placeholder,
  required,
  defaultValue,
  ariaLabel,
  multiple,
  hideBorder,
  hideBackground,
  highlightPlaceholder,
  alert,
  button,
  buttonText,
  buttonOnChange,
  selectedOption,
  selectedOptions,
  tooltipText,
  className,
  dataCy,
  separator,
}) => {
  if (
    options &&
    new Set(options.map((option) => option.id)).size < options.length
  ) {
    throw new Error(
      'The id field must be unique for all options supplied to the Dropdown component',
    );
  }

  if (multiple && options) {
    options = options.map((option) => ({
      checked: option.checked !== undefined ? option.checked : false,
      ...option,
    }));
  }

  const [isToggled, setToggle] = useState<boolean>(false);
  const [value, setValue] = useState<string>(
    defaultValue instanceof Array
      ? defaultValue.map((option) => option.navn).toString()
      : defaultValue
      ? defaultValue.navn
      : '',
  );

  const [currentOptionId, setCurrentOptionId] = useState<string>('');
  const [multipleValueArray, setMultipleValueArray] = useState<
    DropdownOption[]
  >(options ? options.filter((option) => option.checked) : []);

  const [dropdownNavigationActivated, setDropdownNavigationActivated] =
    useState<boolean>(false);

  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const setInputFocus = () => {
    inputRef.current && inputRef.current.focus();
  };

  const toggle = useCallback(
    (down: boolean) => {
      setToggle(down);
    },
    [setToggle],
  );

  const activateDropdownNavigation = (value: boolean = true) => {
    !value && toggle(false);
    setDropdownNavigationActivated(value);
  };

  const dropdownOnChangeHandler = (
    event: React.MouseEvent | React.KeyboardEvent,
    option: DropdownOptionFull,
  ) => {
    const element =
      options && options.find((element) => option.id === element.id);

    if (multiple && element) {
      const tempArray = multipleValueArray;
      const tempElement = tempArray.find((value) => value.id === element.id);
      const index = tempElement ? tempArray.indexOf(tempElement) : -1;

      if (index === -1) {
        tempArray.push(element);
      } else {
        tempArray.splice(index, 1);
      }

      setValue(tempArray.map((option) => option.navn).join(separator));
      setMultipleValueArray(tempArray);
      onChange(event, tempArray);
    } else {
      setValue(element ? element.navn : '');
      element && element.navn && onChange(event, element);
    }

    event.stopPropagation();
    if (!multiple) toggle(!isToggled);
    setCurrentOptionId(option.id);
  };

  React.useEffect(() => {
    if (isToggled)
      dropdownRef.current &&
        dropdownRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
  }, [dropdownRef, isToggled]);

  React.useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        toggle(false);
      }
    };

    const handleGlobalKeyEvent = (event: KeyboardEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        toggle(false);
      }
    };

    document.addEventListener('keydown', handleGlobalKeyEvent, true);
    document.addEventListener('click', handleGlobalClick, true);
    return () => {
      document.removeEventListener('click', handleGlobalClick, true);
      document.removeEventListener('keydown', handleGlobalKeyEvent, true);
    };
  }, [dropdownRef, toggle]);

  React.useEffect(() => {
    if (selectedOption && selectedOption.navn !== value)
      setValue(selectedOption.navn);
    if (selectedOptions) {
      const checkedOptions = selectedOptions.filter((option) => option.checked);

      if (checkedOptions !== multipleValueArray) {
        setMultipleValueArray(selectedOptions);
        setValue(checkedOptions.map((option) => option.navn).join(separator));
      }
    }
  }, [selectedOption, selectedOptions, value, multipleValueArray, separator]);

  return (
    <div
      className={classNames(css.dropdown, className, disabled && css.disabled)}
      onKeyDown={(e) => {
        if (['Enter', 'Space'].includes(e.key)) toggle(!isToggled);
        else if (['Escape', 'Esc'].includes(e.key)) toggle(false);
        else if (['ArrowDown', 'Down'].includes(e.key)) {
          toggle(true);
          activateDropdownNavigation();
        }
      }}
      onClick={() => toggle(!isToggled)}
      ref={dropdownRef}
    >
      <DropdownTextInput
        value={isToggled ? placeholder || value : value}
        placeholder={placeholder}
        label={label}
        htmlId={`${id}-dropdown-input`}
        ariaLabel={ariaLabel}
        toggled={isToggled}
        setToggle={toggle}
        inputRef={inputRef}
        setInputFocus={setInputFocus}
        disabled={disabled}
        required={required}
        currentOptionId={currentOptionId}
        multiSelectable={multiple}
        hideBorder={hideBorder}
        hideBackground={hideBackground}
        highlightPlaceholder={highlightPlaceholder}
        alert={alert}
        dataCy={dataCy}
      />
      {isToggled && (
        <DropdownMenu
          id={`${id}-dropdown-menu`}
          options={
            options && !disabled
              ? options.map((option) => ({
                  ...option,
                  checked:
                    option.checked !== undefined
                      ? multipleValueArray.find(
                          (element) => element.id === option.id,
                        ) !== undefined
                      : undefined,
                }))
              : options
          }
          renderOption={renderOption}
          onChange={dropdownOnChangeHandler}
          activated={dropdownNavigationActivated}
          activateDropdownNavigation={activateDropdownNavigation}
          setInputFocus={setInputFocus}
          noOptionsText={noOptionsText}
          loading={loading}
          multiple={multiple}
          currentValue={value}
          button={button}
          buttonText={buttonText}
          buttonOnChange={buttonOnChange}
          tooltipText={tooltipText}
        />
      )}
    </div>
  );
};

export default Dropdown;
